import { useLazyQuery } from "@apollo/client";
import {
  CircularProgress,
  CssBaseline,
  Fab,
  Grid,
  Typography,
  Hidden,
} from "@mui/material";
import { MUIDataTableOptions } from "mui-datatables";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ApolloError } from "@apollo/client";
import { IAppState } from "../../../../../store";
import { NuvvenTable } from "../../../../common/NuvvenTable/NuvvenTable";
import { useNavigate } from "react-router-dom";
import { SelectableRows } from "../../../../common/NuvvenTable/types";
import { GET_USER_ACCESS_ROLES } from "../../../../../graphql/userPermissions/userAccessQuery";
import { useSnackBar } from "../../../../common/SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../../common/SnackbarWrapper/SnackbarWrapper";
import { DATE_TYPE, formatGraphQLErrorMessage, returnSortedOrder, setTableSortOrder } from "../../../../common/utils";
import { getLocalizedDateFormat } from "../../../../../utils/localized.syntex";
import { UserAccessType, UserAccessTypeVaue } from "./Policies";
import { ITable, TableNames } from "../../../../../reducers/tableState/types";
import { addTableState } from "../../../../../actions/tableState/actions";

export interface IRowUserRole {
  id: string;
  name: string;
  key: string;
  policies: string[];
  createdAt: string;
  updatedAt: string;
}

export const Roles: FC = () => {
  const snackbar = useSnackBar();
  const navigate = useNavigate();
  const userState = useSelector((state: IAppState) => state.userReducer);
  const { country } = userState.currentOrganisation.address;
  const [rows, setRows] = useState<IRowUserRole[]>([]);
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [rolessTable, setRolessTable] = useState<ITable>();
  const rolesTableState = useSelector(
    (state: IAppState) => state.tableStateReducer.roles
  );
  const [sortOrder, setSortOrder] = useState<string | undefined>();

  const [getUserAccessRoles, { loading, data }] = useLazyQuery(GET_USER_ACCESS_ROLES, {
    fetchPolicy: "network-only",
    onCompleted: (data) => { },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  useEffect(() => {
    getUserAccessRoles();
  }, [userState])

  useEffect(() => {
    if (data && data.getUserAccessRoles) {
      setRows(data.getUserAccessRoles)
    }
  }, [data])

  useEffect(() => {
    if (rolesTableState) {
      setRolessTable(rolesTableState);
      let sortedOrder = returnSortedOrder(rolesTableState);
      if (sortedOrder) {
        setSortOrder(sortedOrder);
        setTableSortOrder(columns, rolesTableState, sortedOrder)
      }
    }
  }, [rolesTableState]);

  const columns = [
    {
      label: "Role Name",
      name: "name"
    },
    {
      label: "Created At",
      name: "createdAt",
      options: {
        customBodyRender: (value: string) => getLocalizedDateFormat(country, value, DATE_TYPE.EXPANDED)
      }
    },
    {
      label: "Updated At",
      name: "updatedAt",
      options: {
        customBodyRender: (value: string) => getLocalizedDateFormat(country, value, DATE_TYPE.EXPANDED)
      }
    },
    {
      label: "Defined By",
      name: "roleType",
      options: {
        customBodyRender: (value: UserAccessType) => UserAccessTypeVaue[value]
      }
    },
    {
      label: "Status",
      name: "isActive",
      options: {
        customBodyRender: (value: string) => value ? "Active" : "In-active"
      }
    },
  ];

  const options: MUIDataTableOptions = {
    selectableRows: SelectableRows.NONE,
    onRowClick: (rowData: any, rowMeta: any) => {
      const item: any = rows[rowMeta.dataIndex];
      if (item) {
        navigate(`/update-role?role=${item.id}`, { state: { roleType: item.roleType } });
      }
    },
    onChangeRowsPerPage: (numberOfRows: number) => {
      setLimit(numberOfRows)      
      dispatch(
        addTableState({
          tableName: TableNames.ROLES,
          rowsPerPage: numberOfRows
        })
      );
    },
    rowsPerPage:
    rolessTable && rolessTable.rowsPerPage
        ? rolessTable.rowsPerPage
        : limit,
  }

  return (
    <div>
      <Grid container spacing={2}>
        <CssBaseline />
        <Grid container item xs={6} alignItems="center">
          <Typography variant="h1" color="primary">
            {"  "}Roles{"  "}
          </Typography>
        </Grid>
        <Grid container item xs={6} justifyContent="flex-end">
          <Fab
            variant="extended"
            size="medium"
            aria-label="Add"
            onClick={() => {
              navigate("/new-role");
            }}
          >
            Create
          </Fab>
        </Grid>
        <Grid container item xs={12}>
          {loading ? <CircularProgress /> : (
            <NuvvenTable
              title={""}
              rows={rows}
              columns={columns}
              setSelection={() => { }}
              options={options}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
}